import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import Tooltip from '../common/Tooltip';
import LogHealthPanel from './LogHealthPanel';

const LOG_COLLECTIONS = [
  { key: 'logsCustomer', label: 'Customer Logs', settingId: 'log_retention_logsCustomer' },
  { key: 'logsOpp', label: 'Opportunity Logs', settingId: 'log_retention_logsOpp' },
  { key: 'logsTask', label: 'Task Logs', settingId: 'log_retention_logsTask' },
  { key: 'logsContact', label: 'Contact Logs', settingId: 'log_retention_logsContact' },
  { key: 'logsUser', label: 'User Logs', settingId: 'log_retention_logsUser' },
  { key: 'logsOrder', label: 'Order Logs', settingId: 'log_retention_logsOrder' },
  { key: 'securityLogs', label: 'Security Logs', settingId: 'log_retention_securityLogs' },
  { key: 'logsCalendar', label: 'Calendar Logs', settingId: 'log_retention_logsCalendar' },
];

const LogCleanupTab = ({ user, isReadOnly, setToast }) => {
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupResults, setCleanupResults] = useState(null);
  const [showCleanupResults, setShowCleanupResults] = useState(false);

  // Per-collection retention settings
  const [globalRetention, setGlobalRetention] = useState(365);
  const [retentionSettings, setRetentionSettings] = useState({});
  const [editingRetention, setEditingRetention] = useState({});
  const [savingRetention, setSavingRetention] = useState(null);
  const [retentionLoading, setRetentionLoading] = useState(true);

  // Load retention settings on mount
  useEffect(() => {
    const loadRetentionSettings = async () => {
      try {
        // Load global default
        const globalDoc = await getDoc(doc(db, collectionPath('systemSettings'), 'log_retention_days'));
        if (globalDoc.exists() && globalDoc.data().value != null) {
          const val = parseInt(globalDoc.data().value, 10);
          if (!isNaN(val) && val >= 1 && val <= 1825) setGlobalRetention(val);
        }

        // Load per-collection settings in parallel
        const settings = {};
        await Promise.all(LOG_COLLECTIONS.map(async ({ key, settingId }) => {
          const d = await getDoc(doc(db, collectionPath('systemSettings'), settingId));
          if (d.exists() && d.data().value != null) {
            const val = parseInt(d.data().value, 10);
            if (!isNaN(val) && val >= 1 && val <= 1825) {
              settings[key] = val;
            }
          }
        }));
        setRetentionSettings(settings);
      } catch (error) {
        console.error('Error loading retention settings:', error);
      } finally {
        setRetentionLoading(false);
      }
    };
    loadRetentionSettings();
  }, []);

  const handleSaveRetention = async (collectionKey, settingId, value) => {
    const parsed = parseInt(value, 10);
    if (isNaN(parsed) || parsed < 1 || parsed > 1825) {
      setToast({ message: 'Retention must be between 1 and 1825 days.', type: 'error' });
      return;
    }

    setSavingRetention(collectionKey);
    try {
      await setDoc(doc(db, collectionPath('systemSettings'), settingId), {
        name: `Log Retention - ${LOG_COLLECTIONS.find(c => c.key === collectionKey)?.label}`,
        category: 'config',
        type: 'number',
        description: `Days to retain ${LOG_COLLECTIONS.find(c => c.key === collectionKey)?.label} before automatic deletion`,
        value: parsed,
        updatedAt: Timestamp.now(),
        updatedBy: user?.uid || '',
        updatedByName: user?.displayName || user?.email || '',
      }, { merge: true });

      setRetentionSettings(prev => ({ ...prev, [collectionKey]: parsed }));
      setEditingRetention(prev => { const n = { ...prev }; delete n[collectionKey]; return n; });
      setToast({ message: `Retention for ${LOG_COLLECTIONS.find(c => c.key === collectionKey)?.label} set to ${parsed} days.`, type: 'success' });
    } catch (error) {
      setToast({ message: `Failed to save: ${error.message}`, type: 'error' });
    } finally {
      setSavingRetention(null);
    }
  };

  const handleSaveGlobalRetention = async (value) => {
    const parsed = parseInt(value, 10);
    if (isNaN(parsed) || parsed < 1 || parsed > 1825) {
      setToast({ message: 'Retention must be between 1 and 1825 days.', type: 'error' });
      return;
    }

    setSavingRetention('global');
    try {
      await setDoc(doc(db, collectionPath('systemSettings'), 'log_retention_days'), {
        name: 'Log Retention Days (Global Default)',
        category: 'config',
        type: 'number',
        description: 'Default days to retain logs before automatic deletion. Per-collection settings override this.',
        value: parsed,
        updatedAt: Timestamp.now(),
        updatedBy: user?.uid || '',
        updatedByName: user?.displayName || user?.email || '',
      }, { merge: true });

      setGlobalRetention(parsed);
      setEditingRetention(prev => { const n = { ...prev }; delete n.global; return n; });
      setToast({ message: `Global default retention set to ${parsed} days.`, type: 'success' });
    } catch (error) {
      setToast({ message: `Failed to save: ${error.message}`, type: 'error' });
    } finally {
      setSavingRetention(null);
    }
  };

  const handleResetToGlobal = async (collectionKey, settingId) => {
    setSavingRetention(collectionKey);
    try {
      // Remove the per-collection override by setting value to null
      await setDoc(doc(db, collectionPath('systemSettings'), settingId), {
        value: null,
        updatedAt: Timestamp.now(),
        updatedBy: user?.uid || '',
        updatedByName: user?.displayName || user?.email || '',
      }, { merge: true });

      setRetentionSettings(prev => { const n = { ...prev }; delete n[collectionKey]; return n; });
      setEditingRetention(prev => { const n = { ...prev }; delete n[collectionKey]; return n; });
      setToast({ message: `${LOG_COLLECTIONS.find(c => c.key === collectionKey)?.label} reset to global default (${globalRetention} days).`, type: 'success' });
    } catch (error) {
      setToast({ message: `Failed to reset: ${error.message}`, type: 'error' });
    } finally {
      setSavingRetention(null);
    }
  };

  const handleTriggerLogCleanup = async (dryRun = false) => {
    setCleanupRunning(true);
    setCleanupResults(null);

    try {
      const functions = getFunctions();
      const triggerLogCleanup = httpsCallable(functions, 'triggerLogCleanup');

      const result = await triggerLogCleanup({ dryRun });

      setCleanupResults(result.data);
      setShowCleanupResults(true);

      if (dryRun) {
        setToast({
          message: `Dry run complete. ${result.data.totalDeleted || 0} logs would be deleted.`,
          type: 'success'
        });
      } else {
        setToast({
          message: `Log cleanup complete. ${result.data.totalDeleted || 0} logs deleted.`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Log cleanup failed:', error);
      setToast({
        message: `Log cleanup failed: ${error.message}`,
        type: 'error'
      });
    } finally {
      setCleanupRunning(false);
    }
  };

  return (
    <div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Log Cleanup</h3>
        <p className="text-sm text-gray-600 mb-6">
          Manually trigger log cleanup to remove old logs based on retention settings.
          Logs are automatically cleaned up monthly, but you can run cleanup on-demand.
        </p>

        {isReadOnly ? (
          <p className="text-sm text-amber-600 mb-6">🔒 View-only mode — log cleanup actions are disabled.</p>
        ) : (
          <div className="flex flex-wrap gap-3 mb-6">
            <Tooltip text="Preview what would be deleted without actually deleting">
              <button
                onClick={() => handleTriggerLogCleanup(true)}
                disabled={cleanupRunning}
                className="px-6 py-3 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 font-medium"
              >
                {cleanupRunning ? (
                  <>
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Running...
                  </>
                ) : (
                  <>
                    <span>👁️</span> Preview Cleanup (Dry Run)
                  </>
                )}
              </button>
            </Tooltip>

            <Tooltip text="Delete logs older than retention period - cannot be undone">
              <button
                onClick={() => {
                  const confirmed = window.confirm(
                    'Are you sure you want to delete old logs?\n\n' +
                    'This will permanently delete all logs older than the configured retention period. ' +
                    'This action cannot be undone.\n\n' +
                    'Tip: Run a "Dry Run" first to see what would be deleted.'
                  );
                  if (confirmed) {
                    handleTriggerLogCleanup(false);
                  }
                }}
                disabled={cleanupRunning}
                className="px-6 py-3 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 font-medium"
              >
                {cleanupRunning ? (
                  <>
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  <>
                    <span>🗑️</span> Delete Old Logs Now
                  </>
                )}
              </button>
            </Tooltip>
          </div>
        )}

        {showCleanupResults && cleanupResults && (
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex justify-between items-start mb-3">
              <h4 className="font-semibold text-gray-800">
                {cleanupResults.dryRun ? '👁️ Dry Run Results' : '✅ Cleanup Results'}
              </h4>
              <button
                onClick={() => setShowCleanupResults(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="text-sm mb-3">
              <span className="font-medium">Total {cleanupResults.dryRun ? 'to delete' : 'deleted'}:</span>{' '}
              <span className="text-indigo-600 font-bold">{cleanupResults.totalDeleted || 0}</span> logs
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {cleanupResults.collections && Object.entries(cleanupResults.collections).map(([key, info]) => (
                <div key={key} className="p-3 bg-white rounded border">
                  <div className="font-medium text-gray-800 mb-1">{info.name || key}</div>
                  <div className="text-sm text-gray-600">
                    Retention: {info.retentionDays} days
                  </div>
                  <div className={`text-sm font-medium mt-1 ${info.deleted > 0 || info.toDeleteCount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {cleanupResults.dryRun
                      ? `${info.toDeleteCount || 0} would be deleted`
                      : `${info.deleted || 0} deleted`
                    }
                  </div>
                  {info.error && (
                    <div className="text-xs text-red-500 mt-1">Error: {info.error}</div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-3 text-xs text-gray-500">
              Triggered at: {new Date(cleanupResults.triggeredAt).toLocaleString()}
            </div>
          </div>
        )}

        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-semibold text-blue-900 mb-2">📋 Log Retention Settings</h4>
          <p className="text-sm text-blue-800 mb-4">
            Configure retention periods per collection. Collections without a custom setting use the global default.
            Automatic cleanup runs monthly on the 1st at 2 AM EST.
          </p>

          {retentionLoading ? (
            <p className="text-sm text-blue-600 animate-pulse">Loading retention settings...</p>
          ) : (
            <div className="space-y-3">
              {/* Global Default */}
              <div className="p-3 bg-white rounded-lg border border-blue-200">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="font-medium text-gray-900">🌐 Global Default</span>
                    <p className="text-xs text-gray-500">Applies to all collections without a custom setting</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {editingRetention.global != null ? (
                      <>
                        <input
                          type="number"
                          value={editingRetention.global}
                          onChange={e => setEditingRetention(prev => ({ ...prev, global: e.target.value }))}
                          min={1}
                          max={1825}
                          className="w-20 border border-gray-300 rounded px-2 py-1 text-sm text-center focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <span className="text-xs text-gray-500">days</span>
                        <button
                          onClick={() => handleSaveGlobalRetention(editingRetention.global)}
                          disabled={savingRetention === 'global'}
                          className="px-2 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 disabled:opacity-50"
                        >
                          {savingRetention === 'global' ? '...' : 'Save'}
                        </button>
                        <button
                          onClick={() => setEditingRetention(prev => { const n = { ...prev }; delete n.global; return n; })}
                          className="px-2 py-1 text-gray-500 hover:text-gray-700 text-xs"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="text-sm font-semibold text-indigo-700">{globalRetention} days</span>
                        {!isReadOnly && (
                          <Tooltip text="Edit global default retention">
                            <button
                              onClick={() => setEditingRetention(prev => ({ ...prev, global: globalRetention }))}
                              className="px-2 py-1 text-xs text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                            >
                              ✏️
                            </button>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Per-Collection Settings */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {LOG_COLLECTIONS.map(({ key, label, settingId }) => {
                  const hasCustom = retentionSettings[key] != null;
                  const effectiveDays = hasCustom ? retentionSettings[key] : globalRetention;
                  const isEditing = editingRetention[key] != null;

                  return (
                    <div key={key} className={`p-3 bg-white rounded-lg border ${hasCustom ? 'border-indigo-200' : 'border-gray-200'}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <span className="font-medium text-gray-800 text-sm">{label}</span>
                          {hasCustom && (
                            <span className="ml-1.5 px-1.5 py-0.5 bg-indigo-100 text-indigo-600 rounded text-[10px] font-medium">Custom</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {isEditing ? (
                            <>
                              <input
                                type="number"
                                value={editingRetention[key]}
                                onChange={e => setEditingRetention(prev => ({ ...prev, [key]: e.target.value }))}
                                min={1}
                                max={1825}
                                className="w-16 border border-gray-300 rounded px-2 py-1 text-xs text-center focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              />
                              <button
                                onClick={() => handleSaveRetention(key, settingId, editingRetention[key])}
                                disabled={savingRetention === key}
                                className="px-1.5 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 disabled:opacity-50"
                              >
                                {savingRetention === key ? '...' : '✓'}
                              </button>
                              <button
                                onClick={() => setEditingRetention(prev => { const n = { ...prev }; delete n[key]; return n; })}
                                className="px-1.5 py-1 text-gray-400 hover:text-gray-600 text-xs"
                              >
                                ✕
                              </button>
                            </>
                          ) : (
                            <>
                              <span className={`text-sm font-medium ${hasCustom ? 'text-indigo-700' : 'text-gray-600'}`}>
                                {effectiveDays}d
                              </span>
                              {!isReadOnly && (
                                <>
                                  <Tooltip text="Set custom retention for this collection">
                                    <button
                                      onClick={() => setEditingRetention(prev => ({ ...prev, [key]: effectiveDays }))}
                                      className="px-1 py-0.5 text-xs text-gray-400 hover:text-indigo-600 transition-colors"
                                    >
                                      ✏️
                                    </button>
                                  </Tooltip>
                                  {hasCustom && (
                                    <Tooltip text="Reset to global default">
                                      <button
                                        onClick={() => handleResetToGlobal(key, settingId)}
                                        disabled={savingRetention === key}
                                        className="px-1 py-0.5 text-xs text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                                      >
                                        ↺
                                      </button>
                                    </Tooltip>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <p className="text-xs text-blue-600 mt-2">
                Range: 1–1,825 days (up to ~5 years). Per-collection settings override the global default for both scheduled and manual cleanup.
              </p>
            </div>
          )}
        </div>
      </div>
      <LogHealthPanel user={user} isReadOnly={isReadOnly} setToast={setToast} />
    </div>
  );
};

export default LogCleanupTab;
